import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import RetailerModal from "./Components/RetailerModal";
import api from "../../api";
import { IMAGE_URL } from "../../path";
import "../../styles/Login.scss";

interface Retailer {
    id: number;
    name: string;
    logo: string | null;
    link: string;
    active: boolean;
    display_order: number;
    created_at?: string;
    updated_at?: string;
}

const AdminRetailers: React.FC = () => {
    const [retailers, setRetailers] = useState<Retailer[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [selectedRetailer, setSelectedRetailer] =
        useState<Retailer | null>(null);

    const [showRetailerModal, setShowRetailerModal] =
        useState(false);

    const [deletingRetailerId, setDeletingRetailerId] =
        useState<number | null>(null);

    const [movingRetailerId, setMovingRetailerId] =
        useState<number | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            const response = await api.get(
                "/admin/retailers"
            );

            const data =
                response.data.data ?? response.data;

            setRetailers(
                [...data].sort(
                    (
                        a: Retailer,
                        b: Retailer
                    ) =>
                        a.display_order -
                        b.display_order ||
                        a.id - b.id
                )
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to load retailers."
            );
        } finally {
            setLoading(false);
        }
    };

    /*
     * =========================================================
     * RETAILER MODAL
     * =========================================================
     */

    const handleAddRetailer = () => {
        setSelectedRetailer(null);
        setShowRetailerModal(true);
        setError("");
        setSuccess("");
    };

    const handleEditRetailer = (
        retailer: Retailer
    ) => {
        setSelectedRetailer(retailer);
        setShowRetailerModal(true);
        setError("");
        setSuccess("");
    };

    const handleCloseRetailerModal = () => {
        setShowRetailerModal(false);
        setSelectedRetailer(null);
    };

    const handleRetailerSaved = async () => {
        handleCloseRetailerModal();

        setSuccess(
            "Retailer saved successfully."
        );

        await loadData();
    };

    /*
     * =========================================================
     * RETAILER DELETE
     * =========================================================
     */

    const handleDeleteRetailer = async (
        retailer: Retailer
    ) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${retailer.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingRetailerId(
                retailer.id
            );
            setError("");
            setSuccess("");

            await api.delete(
                `/admin/retailers/${retailer.id}`
            );

            setSuccess(
                "Retailer deleted successfully."
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to delete retailer."
            );
        } finally {
            setDeletingRetailerId(null);
        }
    };

    /*
     * =========================================================
     * MOVE RETAILER
     * =========================================================
     */

    const handleMoveRetailer = async (
        retailer: Retailer,
        direction: "up" | "down"
    ) => {
        try {
            setMovingRetailerId(
                retailer.id
            );
            setError("");
            setSuccess("");

            await api.post(
                `/admin/retailers/${retailer.id}/move`,
                {
                    direction,
                }
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to move retailer."
            );
        } finally {
            setMovingRetailerId(null);
        }
    };

    /*
     * =========================================================
     * RETAILER MOVE STATE
     * =========================================================
     */

    const canMoveRetailerUp = (
        index: number
    ) => {
        return index > 0;
    };

    const canMoveRetailerDown = (
        index: number
    ) => {
        return (
            index <
            retailers.length - 1
        );
    };

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <div className="admin-page">
            <AdminHeader />

            <div className="admin-body">
                <AdminSidebar />

                <main className="admin-content">
                    <div className="admin-users">

                        {/* =================================================
                            PAGE HEADER
                        ================================================== */}

                        <div className="admin-page-header">
                            <div>
                                <h1>Retailers</h1>

                                <p>
                                    Manage the retailers
                                    displayed on the website.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="admin-primary-button"
                                onClick={
                                    handleAddRetailer
                                }
                            >
                                Add Retailer
                            </button>
                        </div>

                        {/* =================================================
                            MESSAGES
                        ================================================== */}

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {loading ? (
                            <p>
                                Loading retailers...
                            </p>
                        ) : (
                            <section className="admin-content-section">

                                {/* =================================================
                                    RETAILERS
                                ================================================== */}

                                <div className="admin-page-header">
                                    <div>
                                        <h2>Retailers</h2>

                                        <p>
                                            Manage the retailers
                                            available to customers.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        className="admin-primary-button"
                                        onClick={
                                            handleAddRetailer
                                        }
                                    >
                                        Add Retailer
                                    </button>
                                </div>

                                {retailers.length === 0 ? (
                                    <p>
                                        No retailers found.
                                    </p>
                                ) : (
                                    <div className="admin-users-table">
                                        <table>
                                            <thead>
                                            <tr>
                                                <th>
                                                    Logo
                                                </th>

                                                <th>
                                                    Retailer
                                                </th>

                                                <th>
                                                    Product Page
                                                </th>

                                                <th>
                                                    Active
                                                </th>

                                                <th>
                                                    Actions
                                                </th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {retailers.map(
                                                (
                                                    retailer,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            retailer.id
                                                        }
                                                    >
                                                        <td>
                                                            {retailer.logo ? (
                                                                <img
                                                                    src={`${IMAGE_URL}/${retailer.logo}`}
                                                                    alt={`${retailer.name} logo`}
                                                                    className="admin-svg-preview"
                                                                />
                                                            ) : (
                                                                <span>
                                                                        —
                                                                    </span>
                                                            )}
                                                        </td>

                                                        <td>
                                                            <strong>
                                                                {
                                                                    retailer.name
                                                                }
                                                            </strong>
                                                        </td>

                                                        <td>
                                                            <a
                                                                href={
                                                                    retailer.link
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                View Product Page
                                                            </a>
                                                        </td>

                                                        <td>
                                                                <span
                                                                    className={`admin-role ${
                                                                        retailer.active
                                                                            ? "admin-role-admin"
                                                                            : "admin-role-editor"
                                                                    }`}
                                                                >
                                                                    {
                                                                        retailer.active
                                                                            ? "Active"
                                                                            : "Inactive"
                                                                    }
                                                                </span>
                                                        </td>

                                                        <td>
                                                            <div className="admin-user-actions">

                                                                <button
                                                                    type="button"
                                                                    className="admin-table-button"
                                                                    disabled={
                                                                        movingRetailerId ===
                                                                        retailer.id ||
                                                                        !canMoveRetailerUp(
                                                                            index
                                                                        )
                                                                    }
                                                                    onClick={() =>
                                                                        handleMoveRetailer(
                                                                            retailer,
                                                                            "up"
                                                                        )
                                                                    }
                                                                >
                                                                    ↑
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="admin-table-button"
                                                                    disabled={
                                                                        movingRetailerId ===
                                                                        retailer.id ||
                                                                        !canMoveRetailerDown(
                                                                            index
                                                                        )
                                                                    }
                                                                    onClick={() =>
                                                                        handleMoveRetailer(
                                                                            retailer,
                                                                            "down"
                                                                        )
                                                                    }
                                                                >
                                                                    ↓
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="admin-table-button"
                                                                    onClick={() =>
                                                                        handleEditRetailer(
                                                                            retailer
                                                                        )
                                                                    }
                                                                >
                                                                    Edit
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="admin-table-button admin-table-button-danger"
                                                                    disabled={
                                                                        deletingRetailerId ===
                                                                        retailer.id
                                                                    }
                                                                    onClick={() =>
                                                                        handleDeleteRetailer(
                                                                            retailer
                                                                        )
                                                                    }
                                                                >
                                                                    {deletingRetailerId ===
                                                                    retailer.id
                                                                        ? "Deleting..."
                                                                        : "Delete"}
                                                                </button>

                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </main>
            </div>

            {/* =========================================================
                RETAILER MODAL
            ========================================================= */}

            {showRetailerModal && (
                <RetailerModal
                    retailer={
                        selectedRetailer
                    }
                    onClose={
                        handleCloseRetailerModal
                    }
                    onSaved={
                        handleRetailerSaved
                    }
                />
            )}
        </div>
    );
};

export default AdminRetailers;
